import { Button, Card, Col, Divider, Dropdown, Form, Input, notification, Row, Skeleton, Space } from "antd";
import { RequiredMark } from "antd/lib/form/Form";
import { Fragment, useEffect, useState } from "react";
import { getCloudService, getEnvironmentService } from "../../backend";
import { DownOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CreateStageRequestCloudProviderEnum, CreateStageRequestInfrastructureAsCodeToolEnum } from "@microtica/ms-engine-sdk";
import { Environment, EnvironmentDetails, FormState } from "../../types";
import { AWS_REGIONS } from "../../enums/enums";
import { trackEnvCreateFailed, trackEnvCreateInit, trackEnvCreateSucceeded } from "../../backend/tracking/environment";
import { useRecoilState, useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { currentTemplateStepState, latestTemplateStepState, templateState } from "../../recoil/template";
import { useCardClassName } from "../../contexts/Helpers/helpers";
import { useAuth } from "../../contexts/Auth";
import { trackTemplateEnvironmentSelect } from "../../backend/tracking/templates";
import { parseTemplatesRepositoryUrl } from "../../utils/parse-templates-repository-url";
import { getEnvironmentsLimit } from "../../utils/local-storage";
import AwsAccountSelect from "../AwsAccountSelect";
import ExplanationButton from "../explanations/ExplanationButton";
import EnvironmentExplanation from "../explanations/EnvironmentExplanation";
import { ItemType } from "antd/lib/menu/hooks/useItems";

interface EnvironmentTemplateModuleProps {
    value?: string;
    templateUrl: string;
    initialEnvironment?: Environment;
    onSelect?: (environment: EnvironmentDetails) => void;
}

const EnvironmentTemplateModule = ({
    value,
    templateUrl,
    initialEnvironment,
    onSelect
}: EnvironmentTemplateModuleProps) => {
    const [form] = Form.useForm();
    const { isLoggedIn } = useAuth();
    const currentProject = useRecoilValue(currentProjectState);
    const [requiredMark] = useState<RequiredMark>('optional');
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [selectedEnvironment, setSelectedEnvironment] = useState(initialEnvironment);
    const [missingAwsConfigs, setMissingAwsConfigs] = useState(false);
    const [mode, setMode] = useState<"new" | "existing">("existing");
    const [loadingEnvs, setLoadingEnvs] = useState(true);
    const [creatingEnv, setCreatingEnv] = useState(false);
    const [awsAccounts, setAwsAccounts] = useState<{ id: string; name: string }[]>([]);
    const [enableCreateEnvironment, setEnableCreateEnvironment] = useState(true);
    const [currentTemplateState, setCurrentTemplateState] = useRecoilState(templateState);
    const currentTemplateStep = useRecoilValue(currentTemplateStepState);
    const latestTemplateStep = useRecoilValue(latestTemplateStepState);
    const [missingEnvironmentError, setMissingEnvironmentError] = useState<string>();
    const [missingAWSAccountError, setMissingAWSAccountError] = useState<string>();

    useEffect(() => {
        // load the envs again once the current project changes
        loadEnvironments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProject]);

    const cardClassName = useCardClassName(
        currentTemplateState["env"] === "disabled",
        [currentTemplateState, currentTemplateStep]
    );

    const loadEnvironments = async () => {
        try {
            const { data: { stages: environments } } = await getEnvironmentService().getStages(currentProject!.id);
            const environmentsLimit = getEnvironmentsLimit();
            if (environmentsLimit <= environments.length) {
                setEnableCreateEnvironment(false);
            }
            const defaultEnv = environments.length === 1 ? environments[0] : environments.find(env => env.id === value);

            if (defaultEnv) {
                setSelectedEnvironment({
                    id: defaultEnv.id,
                    name: defaultEnv.name,
                    accountId: defaultEnv.awsAccountId,
                    region: AWS_REGIONS.find(r => r.value === defaultEnv.awsRegion)?.id,
                    cloudProvider: defaultEnv.cloudProvider
                });
            }

            setEnvironments(environments.map(env => ({
                id: env.id,
                name: env.name,
                cloudProvider: env.cloudProvider,
                accountId: env.awsAccountId,
                region: env.awsRegion
            })));
            setMode(environments.length > 0 ? "existing" : "new");

        } catch (error) {

        } finally {
            setLoadingEnvs(false);
        }
    }

    const loadAwsAccounts = async () => {
        const { data: { awsAccounts } } = await getCloudService().getAwsAccounts(currentProject!.id);

        setAwsAccounts(awsAccounts.map(acc => ({ id: acc.id, name: acc.accountName })));
    }

    useEffect(() => {
        if (isLoggedIn) {
            loadEnvironments();
            loadAwsAccounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        if (mode === "new") {
            trackEnvCreateInit();
        }
    }, [mode]);

    useEffect(() => {
        const loadData = async () => {
            if (selectedEnvironment) {
                const { data: { awsAccountId, region } } = await getEnvironmentService().getStage(
                    selectedEnvironment.id,
                    currentProject!.id
                );
                if (awsAccountId && region) {
                    setMissingAwsConfigs(false);
                } else {
                    setMissingAwsConfigs(true);
                }
            };
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEnvironment]);

    const handleActiveStepChange = () => {
        setCurrentTemplateState(currentTemplateState => ({
            ...currentTemplateState,
            config: "editing",
            env: latestTemplateStep.index > 2 ? "saved" : "editing"
        }))
    }

    const NewEnvironment = () => (
        <Form
            form={form}
            layout="vertical"
            initialValues={{}}
            onFinish={handleCreateEnvironment}
            requiredMark={requiredMark}
        >
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item
                        name="name"
                        label="Name"
                        required
                        rules={[{ required: true, message: 'Please input environment name!' }]}
                    >
                        <Input placeholder="development" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="description"
                        label="Description"
                        required
                        rules={[{ required: true, message: 'Please input environment description!' }]}
                    >
                        <Input placeholder="My development environment" maxLength={100} showCount />
                    </Form.Item>
                </Col>
            </Row>
            <br />
            <Row>
                <Col span={12} style={{ textAlign: "left" }}>
                    {
                        environments.length > 0 &&
                        <Button type="link" style={{ padding: 0 }} onClick={() => setMode("existing")}>
                            Choose existing environment
                        </Button>
                    }
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    <Button type="primary" htmlType="submit" loading={creatingEnv}>
                        Create
                    </Button>
                </Col>
            </Row>
        </Form>
    );

    const ExistingEnvironment = () => (
        <>
            <Space>
                Environment:
                <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    menu={{
                        placeholder: selectedEnvironment?.name || "Select environment",
                        items: environments
                            .map(env => ({
                                key: env.id,
                                label: env.name,
                                onClick: () => {
                                    if (selectedEnvironment?.id !== env.id) {
                                        setSelectedEnvironment({
                                            ...env,
                                            region: AWS_REGIONS.find(r => r.value === env.region)?.id
                                        });
                                        handleSetTemplateState("editing");
                                        if (missingEnvironmentError) {
                                            setMissingEnvironmentError(undefined);
                                        }
                                    }
                                }
                            }) as ItemType)
                            .concat(enableCreateEnvironment ? [
                                { type: "divider" },
                                {
                                    key: "create-environment",
                                    label: <><PlusCircleOutlined /> Create Environment</>,
                                    onClick: () => setMode("new")
                                }
                            ] : [])
                    }}
                    disabled={currentTemplateState["env"] === "disabled" || !!value}
                    className={missingEnvironmentError ? "error-field-wrapper" : ""}
                >
                    <Button>
                        {selectedEnvironment ? selectedEnvironment.name : "Select environment"} <DownOutlined />
                    </Button>
                </Dropdown>
            </Space>
            {missingEnvironmentError &&
                <div className="error-field-wrapper" style={{ marginLeft: "7em", color: "#ff4d4f" }}>{missingEnvironmentError}</div>
            }
            <br />
            {
                selectedEnvironment && missingAwsConfigs &&
                <>
                    <br />
                    <AwsAccountSelect
                        awsAccounts={awsAccounts}
                        selectedAccount={selectedEnvironment}
                        onSelect={handleOnSelect}
                        errorClassName={missingAWSAccountError ? "error-field-wrapper" : undefined}
                        visibleInfoForCostReport={true}
                    />
                    {missingAWSAccountError &&
                        <div className="error-field-wrapper" style={{ color: "#ff4d4f" }}>{missingAWSAccountError}</div>
                    }
                </>
            }
            <Divider />
            <div className="flex-justify-space-between flex-align-center">
                <Button onClick={handleActiveStepChange}>
                    Back
                </Button>
                <Button
                    type={currentTemplateState["env"] === "editing" ? "primary" : "ghost"}
                    onClick={handleSelectExistingEnvironment}
                    disabled={
                        currentTemplateState["env"] === "disabled"
                    }
                >
                    Next
                </Button>
            </div>
        </>
    );

    const handleOnSelect = ({ accountId, region }: { accountId: string, region: string }) => {
        const destination = {
            ...selectedEnvironment,
            accountId,
            region
        } as Environment;
        setSelectedEnvironment(destination);
        handleSetTemplateState("editing");
        if (missingAWSAccountError && !!destination.accountId && !!destination.region) {
            setMissingAWSAccountError(undefined);
        }
    }

    const handleCreateEnvironment = async (values: { name: string, description: string }) => {
        try {
            setCreatingEnv(true);
            const { data: { id } } = await getEnvironmentService().createStage(
                currentProject!.id,
                {
                    name: values.name,
                    description: values.description,
                    cloudProvider: CreateStageRequestCloudProviderEnum.Aws,
                    infrastructureAsCodeTool: CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation
                });

            await loadEnvironments();
            setMode("existing");
            setSelectedEnvironment({ ...values, id, cloudProvider: CreateStageRequestCloudProviderEnum.Aws });
            notification.success({ message: "New environment created" });
            trackEnvCreateSucceeded(
                id,
                values.name,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false,
                templateUrl
            );
        } catch (error: any) {
            notification.error({
                message: "Cannot create new environment",
                description: error.response.data.message
            });
            trackEnvCreateFailed(
                values.name,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false,
                error.response.data.message,
                templateUrl
            );
        } finally {
            setCreatingEnv(false);
        }
    }

    const handleSelectExistingEnvironment = () => {
        if (!selectedEnvironment) {
            setMissingEnvironmentError("Please select environment");
            return;
        }
        if (!selectedEnvironment.accountId || !selectedEnvironment.region) {
            setMissingAWSAccountError("Please select AWS account and region");
            return;
        }
        const { templateName } = parseTemplatesRepositoryUrl(templateUrl);
        trackTemplateEnvironmentSelect(templateName.join("/"));
        onSelect?.(selectedEnvironment);
        handleSetTemplateState("saved");
    }

    const handleSetTemplateState = (mode: FormState) => {
        setCurrentTemplateState({
            ...currentTemplateState,
            env: mode,
            ...(mode === "saved" && { deploy: "editing" })
        });
    }

    return (
        <Card className={cardClassName}>
            <Card.Meta
                title={
                    <>
                        <span>
                            {
                                mode === "new" ?
                                    "Create Environment" :
                                    "Configure Environment"
                            }
                        </span>
                        <ExplanationButton
                            content={<EnvironmentExplanation />}
                        />
                    </>
                }
                description="Environments help you separate your development and production apps."
            />
            <Divider />
            {
                loadingEnvs ? <Skeleton /> :
                    mode === "new" ? <NewEnvironment /> : <ExistingEnvironment />
            }
        </Card>
    )
}

export default EnvironmentTemplateModule;